/**
 * Utility functions for registration data transformation
 */

import { ISOToCountryName, REGISTRATION_STATES_ELIGIBLE_FOR_VOUCHER } from "../config/RegistrationConfigs";
import DateUtils from "./DateUtils";
import {
  findRaceMerchandise,
  findVariantOption,
  merchandiseLineLabel,
  merchandiseUnitPrice,
  normalizeExtraMerchandise,
  sumExtraMerchandisePrices,
} from "./extraMerchandiseLineItems";

/**
 * Whether the race-level voucher payment deadline (if configured) has not passed yet.
 *
 * @param {Object} race - Race with optional `voucherPaymentDeadline`
 * @param {Date} [now] - Reference time
 * @returns {boolean}
 */
function isGlobalVoucherPaymentDeadlineOpen(race, now = new Date()) {
  if (!race?.voucherPaymentDeadline) return true;
  return now < new Date(race.voucherPaymentDeadline);
}

/**
 * Whether the user may add a voucher for this registration (mirrors backend `add-voucher` states + deadline rules).
 *
 * @param {Object} registration - Registration with `state` and optional `voucher`
 * @param {Object} race - Race with optional `voucherPaymentDeadline`
 * @param {Date} [now] - Reference time for deadline check
 * @returns {boolean}
 */
export function canAddVoucher(registration, race, now = new Date()) {
  if (!registration?.state || registration.voucher?.code) return false;
  if (!REGISTRATION_STATES_ELIGIBLE_FOR_VOUCHER.includes(registration.state)) return false;
  return isGlobalVoucherPaymentDeadlineOpen(race, now);
}

/**
 * Formats country ISO code for display
 * Converts ISO code to country name
 * @param {string} isoCode - ISO country code
 * @returns {string} Country name for display
 */
export function formatCountryForDisplay(isoCode) {
  if (!isoCode) return "-";
  return ISOToCountryName[isoCode] || isoCode;
}

/**
 * Transforms registration form data by clearing falsy values for specific fields
 * @param {Object} formData - Raw form data from AsyncFormBox
 * @returns {Object} Transformed data ready for backend submission
 */
export function transformRegistrationFormData(formData) {
  const transformedData = { ...formData };

  // Only normalize birthNumber when the step actually submitted it. Later wizard steps
  // omit the field; inventing null here would wipe a value already stored on the registration.
  if (Object.prototype.hasOwnProperty.call(formData, "birthNumber")) {
    if (!transformedData.birthNumber || transformedData.birthNumber?.trim() === "") {
      transformedData.birthNumber = null; // potentially non-CZ/SK nationality, optional field
    }
  }
  if (!transformedData.birthdate) {
    delete transformedData.birthdate;
  } else {
    // Always store birthdate as midnight Europe/Prague (calendar day from the picker)
    const pragueMidnight = DateUtils.toPragueMidnightISO(transformedData.birthdate);
    if (pragueMidnight) {
      transformedData.birthdate = pragueMidnight;
    } else {
      delete transformedData.birthdate;
    }
  }
  if (!transformedData.phone || transformedData.birthNumber?.trim() === "") {
    delete transformedData.phone;
  }

  return transformedData;
}

/**
 * Extracts metadata fields from registration form data based on the current step
 * @param {Object} formData - Form data object from registration wizard
 * @param {string} step - Current step key ("personalData", "contactData", or "merchandise")
 * @returns {Object} Object containing only the extracted metadata fields
 */
export function extractUserMetadata(formData, step) {
  const metadata = {};

  if (step === "personalData") {
    // Step 1: Personal data fields
    if (formData.sex !== undefined && formData.sex !== null && formData.sex !== "") {
      metadata.sex = formData.sex;
    }
    if (formData.nationality !== undefined && formData.nationality !== null && formData.nationality !== "") {
      metadata.nationality = formData.nationality;
    }
    if (formData.birthNumber !== undefined && formData.birthNumber !== null && formData.birthNumber !== "") {
      metadata.birthNumber = formData.birthNumber;
    } else {
      metadata.birthNumber = null; // potentially non-CZ/SK nationality, optional field
    }
    if (formData.birthdate !== undefined && formData.birthdate !== null) {
      const isoDate = DateUtils.toPragueMidnightISO(formData.birthdate);
      if (isoDate) {
        metadata.birthdate = isoDate;
      }
    }
    if (
      formData.insuranceCompany !== undefined &&
      formData.insuranceCompany !== null &&
      formData.insuranceCompany !== ""
    ) {
      metadata.insuranceCompany = formData.insuranceCompany;
    }
    if (formData.club !== undefined && formData.club !== null && formData.club !== "") {
      metadata.club = formData.club;
    }
  } else if (step === "contactData") {
    // Step 2: Contact data fields
    if (formData.phone !== undefined && formData.phone !== null && formData.phone !== "") {
      metadata.phone = formData.phone;
    }
    if (formData.street !== undefined && formData.street !== null && formData.street !== "") {
      metadata.street = formData.street;
    }
    if (formData.streetNumber !== undefined && formData.streetNumber !== null && formData.streetNumber !== "") {
      metadata.streetNumber = formData.streetNumber;
    }
    if (formData.zip !== undefined && formData.zip !== null && formData.zip !== "") {
      metadata.zip = formData.zip;
    }
    if (formData.city !== undefined && formData.city !== null && formData.city !== "") {
      metadata.city = formData.city;
    }
    if (formData.country !== undefined && formData.country !== null && formData.country !== "") {
      metadata.country = formData.country;
    }
  } else if (step === "merchandise") {
    // Step 3: Merchandise fields
    if (formData.tshirtSize !== undefined && formData.tshirtSize !== null && formData.tshirtSize !== "") {
      metadata.tshirtSize = formData.tshirtSize;
    }
  }

  return metadata;
}

/**
 * Calculates price breakdown for a registration
 * @param {Object} race - Race object with racePrice, registrationFee, and merchandises
 * @param {Object} registration - Registration object with voucher, master, extraMerchandise, and totalPrice
 * @returns {Object} Price breakdown object with:
 *   - registrationFee: { amount: number, applies: boolean }
 *   - racePrice: { original: number, discounted: number, hasDiscount: boolean }
 *   - merchandise: Array<{ id: string, name: string, price: number, quantity: number }>
 *   - total: number
 */
export function getPriceBreakdown(race, registration) {
  const originalRacePrice = race?.racePrice || 0;
  const discountedRacePrice =
    registration?.voucher?.discount && originalRacePrice
      ? originalRacePrice * (1 - registration.voucher.discount / 100)
      : originalRacePrice;
  const hasVoucherDiscount = registration?.voucher?.discount && originalRacePrice > discountedRacePrice;

  const paysRegistrationFee = race?.registrationFee > 0 && !registration?.master && !registration?.voucher;

  // Get selected merchandise
  const selectedMerchandise = normalizeExtraMerchandise(registration?.extraMerchandise)
    .map(({ merchandiseId, quantity, variantId }) => {
      const merch = findRaceMerchandise(race, merchandiseId);
      if (!merch) return null;
      const option = findVariantOption(merch, variantId);
      const unitPrice = merchandiseUnitPrice(merch, registration?.voucher, variantId);
      return {
        id: merch._id?.toString() || merch._id,
        name: merchandiseLineLabel(merch.name, option?.label),
        price: unitPrice * quantity,
        quantity,
      };
    })
    .filter(Boolean);

  const totalPrice = registration?.totalPrice || 0;

  return {
    registrationFee: {
      amount: race?.registrationFee || 0,
      applies: paysRegistrationFee,
    },
    racePrice: {
      original: originalRacePrice,
      discounted: discountedRacePrice,
      hasDiscount: hasVoucherDiscount,
    },
    merchandise: selectedMerchandise,
    total: totalPrice,
  };
}

/**
 * Sum of the lines from `getPriceBreakdown`. `breakdown.total` is the saved price, not this sum.
 * @param {Object} breakdown - Result of `getPriceBreakdown`
 * @param {boolean} [includeRacePrice] - Include the race price and preregistration fee
 * @returns {number}
 */
export function sumPriceBreakdown(breakdown, { includeRacePrice = true } = {}) {
  const merchandiseTotal = (breakdown?.merchandise || []).reduce((sum, line) => sum + (line.price || 0), 0);
  if (!includeRacePrice) return merchandiseTotal;
  const fee = breakdown?.registrationFee?.applies ? breakdown.registrationFee.amount || 0 : 0;
  return (breakdown?.racePrice?.discounted || 0) + fee + merchandiseTotal;
}

/**
 * Sum selected merchandise prices. A VIP voucher discounts mandatory items.
 * @param {Object} race
 * @param {Object} registration
 * @returns {number}
 */
function sumMerchandisePrices(race, registration) {
  return sumExtraMerchandisePrices(race, registration?.extraMerchandise, registration?.voucher);
}

/**
 * Voucher with a partial discount (0 < discount < 100). Baseline frozen on registration when persisted.
 * @param {Object} registration
 * @returns {boolean}
 */
export function isPartialVoucherRegistration(registration) {
  const code = registration?.voucher?.code;
  const discount = Number(registration?.voucher?.discount);
  return Boolean(code) && discount > 0 && discount < 100;
}

/**
 * @param {Object} race
 * @returns {number}
 */
function resolveCatalogRacePrice(race) {
  return Number(race?.racePrice) || 0;
}

/**
 * @param {Object} race
 * @param {Object} registration
 * @returns {number}
 */
function calculatePartialVoucherMarketAskBaseline(race, registration) {
  const discount = Number(registration.voucher.discount);
  const racePart = resolveCatalogRacePrice(race) * (1 - discount / 100);
  return racePart + sumMerchandisePrices(race, registration);
}

/**
 * @param {Object} race
 * @param {Object} registration
 * @returns {number}
 */
function calculateLiveMarketAskBaseline(race, registration) {
  return resolveCatalogRacePrice(race) + sumMerchandisePrices(race, registration);
}

/**
 * Maximum buyer total (`askPrice + sellFee`) allowed when listing on the marketplace.
 * @param {Object} race
 * @param {Object} registration
 * @returns {number}
 */
export function calculateMarketAskBaseline(race, registration) {
  if (registration?.marketplacePrice != null && Number.isFinite(Number(registration.marketplacePrice))) {
    return Number(registration.marketplacePrice);
  }
  if (isPartialVoucherRegistration(registration)) {
    return calculatePartialVoucherMarketAskBaseline(race, registration);
  }
  return calculateLiveMarketAskBaseline(race, registration);
}

/**
 * Whether preregistration fee is in `totalPrice` but excluded from market baseline.
 * @param {Object} race
 * @param {Object} registration
 * @returns {boolean}
 */
export function paysPreregistrationFeeInTotal(race, registration) {
  const isMasterOrVoucher = registration?.master || (registration?.voucher && registration.voucher.code);
  return !isMasterOrVoucher && Boolean(race?.registrationFee);
}

/**
 * @param {Object} registration
 * @returns {boolean}
 */
export function hasFrozenMarketplacePrice(registration) {
  return registration?.marketplacePrice != null && Number.isFinite(Number(registration.marketplacePrice));
}
